import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Chart, ChartConfiguration, registerables } from 'chart.js';

Chart.register(...registerables);

@Component({
  selector: 'app-conduct-timelogs-summary-card',
  standalone: true,
  imports: [CommonModule, NgxSkeletonLoaderModule, MatTooltipModule],
  templateUrl: './conduct-timelogs-summary-card.component.html',
  styleUrls: ['./conduct-timelogs-summary-card.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConductTimelogsSummaryCardComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('chartCanvas', { static: false }) chartCanvas?: ElementRef<HTMLCanvasElement>;

  @Input() matchPercentage = 0;
  @Input() desktopTimeFormatted = '0h 00m';
  @Input() desktopTimeSubtitle = 'No timeline recorded';
  @Input() conductTimeFormatted = '0h 00m';
  @Input() tasksCount = 0;
  @Input() unloggedTimeFormatted = '0h 00m';
  @Input() unloggedGapsCount = 0;
  @Input() isLoading = false;

  private chart: Chart<'doughnut'> | null = null;

  get ringColor(): string {
    if (this.matchPercentage === 0) {
      return '#cbd5e1'; // Neutral gray ring for 0%
    }
    if (this.matchPercentage >= 80 && !this.isUnloggedPresent) {
      return '#10b981'; // Emerald for healthy match without unlogged gaps
    }
    if (this.matchPercentage >= 80 && this.isUnloggedPresent) {
      return '#f59e0b'; // Amber warning when match is high but unlogged gaps exist
    }
    // Red accent for discrepancy
    return '#dc2626';
  }

  get matchLabel(): string {
    if (this.matchPercentage === 0) {
      return 'No activity';
    }
    return 'matched';
  }

  get isUnloggedPresent(): boolean {
    return !!(
      this.unloggedTimeFormatted &&
      this.unloggedTimeFormatted !== '00h 00m' &&
      this.unloggedTimeFormatted !== '0h 00m' &&
      this.unloggedTimeFormatted !== '00:00' &&
      this.unloggedTimeFormatted !== '0m'
    );
  }

  get unloggedTextColorClass(): string {
    return this.isUnloggedPresent ? 'text-red-600' : 'text-slate-900';
  }

  get displayDesktopTime(): string {
    return this.cleanHourFormat(this.desktopTimeFormatted);
  }

  get displayConductTime(): string {
    return this.cleanHourFormat(this.conductTimeFormatted);
  }

  get displayUnloggedTime(): string {
    return this.cleanHourFormat(this.unloggedTimeFormatted);
  }

  get displayDesktopSubtitle(): string {
    if (!this.desktopTimeSubtitle || this.desktopTimeSubtitle === 'No timeline recorded') {
      return 'No timeline recorded';
    }
    // Convert e.g. "09h 00m prod • 00h 00m idle" -> "9h prod · 0h idle"
    const match = this.desktopTimeSubtitle.match(/(\d+)h\s*(\d+)m\s*prod\s*[•·]\s*(\d+)h\s*(\d+)m\s*idle/i);
    if (match) {
      const prodH = parseInt(match[1], 10);
      const prodM = parseInt(match[2], 10);
      const idleH = parseInt(match[3], 10);
      const idleM = parseInt(match[4], 10);

      const prodStr = prodM > 0 ? `${prodH}h ${prodM}m` : `${prodH}h`;
      const idleStr = idleM > 0 ? (idleH > 0 ? `${idleH}h ${idleM}m` : `${idleM}m`) : `${idleH}h`;
      return `${prodStr} prod · ${idleStr} idle`;
    }
    return this.desktopTimeSubtitle.replace(/•/g, '·');
  }

  private cleanHourFormat(val: string): string {
    if (!val || val === '00:00' || val === '00h 00m') {
      return '0h 00m';
    }
    // Remove leading zero on hours e.g. "09h 00m" -> "9h 00m"
    return val.replace(/^0(\d)h/, '$1h');
  }

  get gapsSubtitle(): string {
    if (this.unloggedGapsCount <= 0) {
      return '0 gaps';
    }
    if (this.unloggedGapsCount === 1) {
      return 'in 1 gap';
    }
    return `in ${this.unloggedGapsCount} gaps`;
  }

  get tasksSubtitle(): string {
    if (this.tasksCount === 1) {
      return 'across 1 task';
    }
    return `across ${this.tasksCount} tasks`;
  }

  ngAfterViewInit(): void {
    this.createOrUpdateChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['matchPercentage'] && !changes['matchPercentage'].firstChange) {
      this.createOrUpdateChart();
    }
  }

  ngOnDestroy(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  private createOrUpdateChart(): void {
    if (!this.chartCanvas?.nativeElement) {
      return;
    }

    const ctx = this.chartCanvas.nativeElement.getContext('2d');
    if (!ctx) {
      return;
    }

    const validMatch = Math.max(0, Math.min(100, Math.round(this.matchPercentage || 0)));
    const remaining = Math.max(0, 100 - validMatch);

    let chartData: number[];
    let bgColors: string[];

    if (validMatch === 0) {
      chartData = [0, 100];
      bgColors = ['#e9ecf2', '#e9ecf2'];
    } else {
      chartData = [validMatch, remaining];
      bgColors = [this.ringColor, '#e9ecf2'];
    }

    if (this.chart) {
      this.chart.data.datasets[0].data = chartData;
      this.chart.data.datasets[0].backgroundColor = bgColors;
      this.chart.update();
      return;
    }

    const config: ChartConfiguration<'doughnut'> = {
      type: 'doughnut',
      data: {
        datasets: [
          {
            data: chartData,
            backgroundColor: bgColors,
            borderWidth: 0,
            hoverOffset: 0,
            borderRadius: 8
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '78%',
        rotation: -90,
        circumference: 360,
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false }
        },
        animation: {
          duration: 500
        }
      }
    };

    this.chart = new Chart(ctx, config);
  }
}
